/**
 * Return Reports Service
 *
 * Data access layer for booking return reports.
 * Handles fetching, filtering, and updating return report data.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import { fetchAllPagesByChunks } from '../lib/fetchAllPages';
import type {
  BookingReturnWithDetails,
  BookingItemReturn,
  BookingReturnPhoto,
  ReturnReportListItem,
  ReturnVersionHistory,
  ReturnReportFilters,
  ReviewStatus,
} from '../types/return-reports';

const ITEMS_PER_PAGE = 20;

/**
 * Get paginated list of return reports
 */
export async function getReturnReports(
  merchantId: string,
  locationIds: string[] | null,
  filters: ReturnReportFilters = {},
  page: number = 1
): Promise<{
  data: ReturnReportListItem[];
  total: number;
  error: string | null;
}> {
  try {
    const offset = (page - 1) * ITEMS_PER_PAGE;

    // Use the view for efficient querying
    let query = supabase
      .from('v_latest_booking_returns')
      .select('*', { count: 'exact' })
      .eq('merchant_id', merchantId);

    // Restrict to the user's locations when scoped. Empty/null = all
    // locations (merchant-wide); never pass [] to .in() (matches nothing).
    if (locationIds && locationIds.length > 0) {
      query = query.in('location_id', locationIds);
    }

    // Apply filters
    if (filters.status && filters.status !== 'all') {
      query = query.eq('overall_status', filters.status);
    }

    if (filters.reviewStatus && filters.reviewStatus !== 'all') {
      query = query.eq('review_status', filters.reviewStatus);
    }

    if (filters.locationId) {
      query = query.eq('location_id', filters.locationId);
    }

    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }

    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }

    if (filters.search) {
      // Search by order_id or customer name
      query = query.or(
        `order_id.ilike.%${filters.search}%,customer_firstname.ilike.%${filters.search}%,customer_lastname.ilike.%${filters.search}%`
      );
    }

    // Order by most recent and paginate
    const { data, count, error } = await query
      .order('created_at', { ascending: false })
      .range(offset, offset + ITEMS_PER_PAGE - 1);

    if (error) throw error;

    // Fetch first photo URL for each return
    const returnIds = (data?.map(r => r.return_id).filter((id): id is string => id != null)) || [];
    let photoMap: Record<string, string> = {};

    if (returnIds.length > 0) {
      // Get first photo for each return (booking-level photos only). Only one
      // page of returns is in play, but a photo-heavy set of them can still
      // carry more than the row cap between them, and the truncated tail would
      // show as missing thumbnails — so the fetch is paged.
      const photos = await fetchAllPagesByChunks<any, string>(
        returnIds,
        (ids, fromRow, toRow) =>
          supabase
            .from('booking_return_photos')
            .select('id, booking_return_id, file_path')
            .in('booking_return_id', ids)
            .is('booking_item_return_id', null)
            .is('replaced_at', null)
            .order('created_at', { ascending: true })
            .order('id', { ascending: true })
            .range(fromRow, toRow)
      );

      // Keep only first photo per return
      for (const photo of photos) {
        const returnId = photo.booking_return_id;
        if (returnId && !photoMap[returnId]) {
          photoMap[returnId] = supabase.storage
            .from('return-pictures')
            .getPublicUrl(photo.file_path).data.publicUrl;
        }
      }
    }

    // Add first_photo_url to each return
    const enrichedData = (data || []).map(item => ({
      ...item,
      first_photo_url: (item.return_id ? photoMap[item.return_id] : null) || null,
    })) as unknown as ReturnReportListItem[];

    return {
      data: enrichedData,
      total: count || 0,
      error: null,
    };
  } catch (error) {
    return {
      data: [],
      total: 0,
      error: handleSupabaseError(error),
    };
  }
}

/**
 * Get a single return report with all details
 */
export async function getReturnReportById(
  returnId: string
): Promise<{
  data: BookingReturnWithDetails | null;
  error: string | null;
}> {
  try {
    // 1. Get main return with joins
    const { data: returnData, error: returnError } = await supabase
      .from('booking_returns')
      .select(`
        *,
        bookings (
          order_id,
          customer_firstname,
          customer_lastname,
          customer_email,
          customer_phone,
          booking_state,
          start_date,
          end_date,
          returned_at
        ),
        merchants (
          business_name,
          display_name
        ),
        locations (
          location_name,
          google_maps_url
        )
      `)
      .eq('id', returnId)
      .single();

    if (returnError) throw returnError;
    if (!returnData) return { data: null, error: 'Return report not found' };

    // 2. Get item returns
    const { data: itemReturns, error: itemError } = await supabase
      .from('booking_item_returns')
      .select(`
        *,
        booking_items (
          item_name,
          product_type,
          size,
          code,
          quantity
        ),
        inventory_items (
          item_number,
          combination_code,
          product_type_id
        )
      `)
      .eq('booking_return_id', returnId);

    if (itemError) throw itemError;

    // 3. Get booking-level photos
    const { data: bookingPhotos, error: bookingPhotosError } = await supabase
      .from('booking_return_photos')
      .select('*')
      .eq('booking_return_id', returnId)
      .is('booking_item_return_id', null)
      .is('replaced_at', null);

    if (bookingPhotosError) throw bookingPhotosError;

    // 4. Get item-level photos
    const { data: itemPhotos, error: itemPhotosError } = await supabase
      .from('booking_return_photos')
      .select('*')
      .eq('booking_return_id', returnId)
      .not('booking_item_return_id', 'is', null)
      .is('replaced_at', null);

    if (itemPhotosError) throw itemPhotosError;

    // Add public URLs to photos
    const addPublicUrls = (photos: BookingReturnPhoto[]): BookingReturnPhoto[] => {
      return photos.map(photo => ({
        ...photo,
        public_url: supabase.storage
          .from('return-pictures')
          .getPublicUrl(photo.file_path).data.publicUrl,
      }));
    };

    const result = {
      ...returnData,
      item_returns: itemReturns as unknown as BookingItemReturn[],
      booking_photos: addPublicUrls(bookingPhotos as unknown as BookingReturnPhoto[]),
      item_photos: addPublicUrls(itemPhotos as unknown as BookingReturnPhoto[]),
    } as unknown as BookingReturnWithDetails;

    return { data: result, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get return report for a specific booking (latest version)
 */
export async function getReturnReportByBookingId(
  bookingId: string
): Promise<{
  data: BookingReturnWithDetails | null;
  error: string | null;
}> {
  try {
    // First get the latest return ID for this booking
    const { data: latestReturn, error: findError } = await supabase
      .from('booking_returns')
      .select('id')
      .eq('booking_id', bookingId)
      .eq('is_latest', true)
      .maybeSingle();

    if (findError) throw findError;
    if (!latestReturn) return { data: null, error: null }; // No return exists

    // Get full details
    return getReturnReportById(latestReturn.id);
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Check if a booking has a return report
 */
export async function hasReturnReport(
  bookingId: string
): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('booking_returns')
      .select('id')
      .eq('booking_id', bookingId)
      .eq('is_latest', true)
      .maybeSingle();

    if (error) throw error;
    return !!data;
  } catch (error) {
    console.error('Error checking for return report:', error);
    return false;
  }
}

/**
 * Get version history for a booking's return reports
 */
export async function getReturnVersionHistory(
  bookingId: string
): Promise<{
  data: ReturnVersionHistory[];
  error: string | null;
}> {
  try {
    const { data, error } = await supabase
      .from('booking_returns')
      .select('id, version, created_at, created_at_vienna, overall_status, is_latest')
      .eq('booking_id', bookingId)
      .order('version', { ascending: true });

    if (error) throw error;

    return { data: (data || []) as unknown as ReturnVersionHistory[], error: null };
  } catch (error) {
    return { data: [], error: handleSupabaseError(error) };
  }
}

/**
 * Mark a return report as reviewed
 */
export async function markAsReviewed(
  returnId: string,
  reviewedBy: string
): Promise<{
  success: boolean;
  error: string | null;
}> {
  try {
    const { error } = await supabase
      .from('booking_returns')
      .update({
        review_status: 'reviewed' as ReviewStatus,
        reviewed_at: new Date().toISOString(),
        reviewed_by: reviewedBy,
      })
      .eq('id', returnId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    return { success: false, error: handleSupabaseError(error) };
  }
}

/**
 * Update internal notes for a return report
 */
export async function updateInternalNotes(
  returnId: string,
  notes: string
): Promise<{
  success: boolean;
  error: string | null;
}> {
  try {
    const { error } = await supabase
      .from('booking_returns')
      .update({
        internal_notes: notes,
      })
      .eq('id', returnId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    return { success: false, error: handleSupabaseError(error) };
  }
}

/**
 * Get photo public URL
 */
export function getPhotoUrl(filePath: string): string {
  return supabase.storage
    .from('return-pictures')
    .getPublicUrl(filePath).data.publicUrl;
}

/**
 * Get items with issues (for maintenance tracking)
 */
export async function getItemsWithIssues(
  _merchantId: string,
  locationId: string | null
): Promise<{
  data: any[];
  error: string | null;
}> {
  try {
    let query = (supabase.from as any)('v_inventory_item_issues')
      .select('*')
      .order('reported_at', { ascending: false });

    // Note: The view should already filter by merchant context
    // Add location filter if applicable
    if (locationId) {
      query = query.eq('location_id', locationId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    return { data: [], error: handleSupabaseError(error) };
  }
}

/**
 * Get return reports count by status (for dashboard)
 */
export async function getReturnReportsCounts(
  merchantId: string,
  locationId: string | null
): Promise<{
  total: number;
  allOk: number;
  issues: number;
  pendingReview: number;
  error: string | null;
}> {
  try {
    // Each tile is a separate exact count rather than one fetch tallied in JS.
    // The old shape asked for the rows AND an exact count, then derived three
    // of the four numbers by filtering `data` — but PostgREST caps `data` at
    // `max-rows` (1000) while `count` stays exact, so past 1000 return reports
    // the tiles silently disagreed with the total they sit next to. Counting in
    // the database is both correct at any size and cheaper: `head: true`
    // transfers no rows at all.
    const countRows = async (
      apply?: (q: ReturnType<typeof baseCount>) => ReturnType<typeof baseCount>
    ) => {
      let query = baseCount();
      if (apply) query = apply(query);
      const { count, error } = await query;
      if (error) throw error;
      return count || 0;
    };

    function baseCount() {
      let query = supabase
        .from('v_latest_booking_returns')
        .select('*', { count: 'exact', head: true })
        .eq('merchant_id', merchantId);
      if (locationId) {
        query = query.eq('location_id', locationId);
      }
      return query;
    }

    const [total, allOk, issues, pendingReview] = await Promise.all([
      countRows(),
      countRows(q => q.eq('overall_status', 'all_ok')),
      countRows(q => q.eq('overall_status', 'issues_reported')),
      countRows(q => q.eq('review_status', 'pending')),
    ]);

    return {
      total,
      allOk,
      issues,
      pendingReview,
      error: null,
    };
  } catch (error) {
    return {
      total: 0,
      allOk: 0,
      issues: 0,
      pendingReview: 0,
      error: handleSupabaseError(error),
    };
  }
}
